import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import api, { fmtDate } from '../../utils/api';
import { notifyError, notifyMessage } from '../../utils/notify';
import * as XLSX from 'xlsx';

const PAGE_SIZE = 50;

export default function SerialNumbersPage() {
  const { i18n } = useTranslation();
  const he = i18n.language === 'he';
  const { hasRole } = useAuth();
  const canEdit = hasRole('admin');

  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchList = useCallback(async (query, pg) => {
    setLoading(true);
    try {
      const res = await api.get('/serial-numbers', { params: { q: query, page: pg, pageSize: PAGE_SIZE } });
      setData(res.data);
    } catch (e) { notifyError(e); } finally { setLoading(false); }
  }, []);

  const fetchSummary = useCallback(async () => {
    try { const res = await api.get('/serial-numbers/summary'); setSummary(res.data); } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  // debounced search + pagination — refetch when q or page changes
  useEffect(() => {
    const t = setTimeout(() => fetchList(q, page), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [q, page, fetchList]);

  const onSearch = (val) => { setQ(val); setPage(1); };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
      const toDate = (d) => { if (!d) return null; const dt = new Date(d); return isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10); };
      const items = rows.map(r => ({
        serial_number: String(r['Serial #'] || r['Serial Number'] || r.serial_number || r.Serial || '').trim(),
        sku: String(r['SKU#'] || r['Vendor P/N'] || r.sku || r.SKU || '').trim(),
        description: String(r['SKU Name'] || r.description || r.Description || '').trim(),
        upc: String(r['EAN #'] || r['UPC Number'] || r.upc || '').trim(),
        carton_id: String(r['MasterCartonID'] || r['Carton ID'] || r.carton_id || '').trim(),
        customer_po: String(r['Zound Industries SO #'] || r['Customer Purchase Order'] || r.customer_po || '').trim(),
        si_number: String(r['Zound Industries PO #'] || r['SI number'] || r.si_number || r.SI || '').trim().toUpperCase(),
        item_limit: parseInt(r.ItemLimit || r.item_limit || r['CartonQuantity'] || 0, 10) || null,
        delivery_date: toDate(r['delivery date'] || r.delivery_date),
      })).filter(x => x.serial_number);
      if (!items.length) { notifyMessage('No valid serials found', 'לא נמצאו מספרים סידוריים בקובץ'); return; }
      let added = 0;
      const CHUNK = 2000;
      for (let i = 0; i < items.length; i += CHUNK) {
        const res = await api.post('/serial-numbers/bulk', { items: items.slice(i, i + CHUNK) });
        added += res.data?.added || 0;
      }
      notifyMessage(`Imported ${added} serials`, `יובאו ${added} מספרים סידוריים`);
      setPage(1); fetchSummary(); fetchList(q, 1);
    } catch (err) { notifyError(err); } finally { setUploading(false); e.target.value = ''; }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const statusColor = (s) => (s === 'active' ? '#16a34a' : s === 'expired' ? '#dc2626' : '#9ca3af');
  const statusLabel = (s) => (he ? ({ active: 'בתוקף', expired: 'פג תוקף', unknown: '—' }[s] || s) : ({ active: 'Active', expired: 'Expired', unknown: '—' }[s] || s));

  const Stat = ({ label, value, color }) => (
    <div style={{ background: 'var(--surface2,#f5f5f5)', borderRadius: 8, padding: '10px 16px', minWidth: 90 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted,#888)' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'DM Mono', monospace", color: color || 'var(--text,#111)' }}>{(value ?? 0).toLocaleString()}</div>
    </div>
  );

  return (
    <div>
      <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1, marginBottom: 16 }}>
        {he ? 'מספרים סידוריים' : 'SERIAL NUMBERS'}
      </h2>

      {summary && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <Stat label={he ? 'סה"כ' : 'Total'} value={summary.total} />
          <Stat label={he ? 'הזמנות' : 'Orders'} value={summary.orders} />
          <Stat label={he ? 'בתוקף' : 'Active'} value={summary.active} color="#16a34a" />
          <Stat label={he ? 'מסתיים ב-90 יום' : 'Expiring 90d'} value={summary.expiring_soon} color="#e65100" />
          <Stat label={he ? 'פג תוקף' : 'Expired'} value={summary.expired} color="#dc2626" />
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <input className="input" type="search"
          placeholder={he ? 'חיפוש לפי מספר סידורי (או חלק ממנו)...' : 'Search by serial number (or part of it)...'}
          value={q} onChange={e => onSearch(e.target.value)} style={{ flex: 1, minWidth: 260, maxWidth: 460 }} />
        {canEdit && (
          <>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={onUpload} />
            <button className="btn btn-gold" disabled={uploading} onClick={() => fileRef.current?.click()}>
              {uploading ? (he ? 'מעלה...' : 'Uploading...') : (he ? '⬆ העלאת רשימת SN' : '⬆ Upload SN list')}
            </button>
          </>
        )}
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr>
            <th>{he ? 'מספר סידורי' : 'Serial Number'}</th>
            <th>{he ? 'דגם' : 'Model'}</th>
            <th>{he ? 'הזמנה' : 'Order'}</th>
            <th>{he ? 'תאריך רכישה ממרשל' : 'Purchase Date'}</th>
            <th>{he ? 'אחריות עד' : 'Warranty Ends'}</th>
            <th className="text-center">{he ? 'סטטוס' : 'Status'}</th>
          </tr></thead>
          <tbody>
            {data.items.map(s => (
              <tr key={s.serial_number}>
                <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{s.serial_number}</td>
                <td><span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{s.sku}</span>
                  <div className="text-muted" style={{ fontSize: 11 }}>{s.description}</div></td>
                <td><span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{s.si_number || '—'}</span>
                  {s.customer_po && <div className="text-muted" style={{ fontSize: 10 }}>{s.customer_po}</div>}</td>
                <td style={{ fontFamily: "'DM Mono', monospace" }}>{s.purchase_date ? fmtDate(s.purchase_date) : '—'}</td>
                <td style={{ fontFamily: "'DM Mono', monospace" }}>{s.warranty_end ? fmtDate(s.warranty_end) : '—'}</td>
                <td className="text-center">
                  <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, color: '#fff', background: statusColor(s.warranty_status) }}>
                    {statusLabel(s.warranty_status)}
                  </span>
                </td>
              </tr>
            ))}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted" style={{ padding: 24 }}>{he ? 'אין תוצאות' : 'No results'}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
        <span className="text-muted" style={{ fontSize: 13 }}>
          {loading ? (he ? 'טוען...' : 'Loading...') : `${data.total.toLocaleString()} ${he ? 'תוצאות' : 'results'}`}
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-sm btn-outline" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>{he ? 'הקודם' : 'Prev'}</button>
          <span style={{ fontSize: 13 }}>{he ? 'עמוד' : 'Page'} {page} / {totalPages}</span>
          <button className="btn btn-sm btn-outline" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>{he ? 'הבא' : 'Next'}</button>
        </div>
      </div>
    </div>
  );
}
